import { Box, createFilterOptions, TextField, TextFieldProps, Tooltip } from "@mui/material";
import { useState } from "react";
import StyledAutocomplete from "./StyledAutocomplete";
import { useAppSelector } from "../redux/hooks";
import { DocumentIntake } from "generated/graphql";
import { P } from "theme/typography";
import truncateString from "helpers/truncateString";
import { displayDocumentStatus, documentStatusText } from "Backend";
import { selectIntakesSortedByStatus } from "redux/slices/case";

const renderIntake = (p: React.HTMLAttributes<HTMLLIElement>, intake: DocumentIntake) => {
  return (
    <li {...p}>
      <Tooltip title={intake.name} placement="right" arrow>
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <P noWrap variant="subtitle2">
            {truncateString(intake.name, 60)}
          </P>
        </Box>
      </Tooltip>
    </li>
  );
};

interface CaseIntakesAutocompleteProps {
  caseId: number;
  label?: string;
  variant?: TextFieldProps["variant"];
  selected?: DocumentIntake;
  onChange: (intake: DocumentIntake) => void;
}

const CaseIntakesAutocomplete = (props: CaseIntakesAutocompleteProps) => {
  const { onChange, label, variant, selected } = props;
  const [textQuery, setTextQuery] = useState<string>("");
  const options = useAppSelector(selectIntakesSortedByStatus);

  return (
    <StyledAutocomplete<DocumentIntake, false, true, false>
      freeSolo={false}
      multiple={false}
      autoHighlight
      autoComplete
      size="small"
      fullWidth
      groupBy={(intake) => displayDocumentStatus(intake.status)}
      options={options}
      value={selected}
      onChange={(_evt, item) => {
        if (item) onChange(item);
      }}
      disableClearable
      inputValue={textQuery}
      onInputChange={(_evt, newInputValue) => setTextQuery(newInputValue)}
      renderOption={renderIntake}
      getOptionLabel={(d: DocumentIntake) => d.name}
      renderInput={(params) => (
        <TextField {...params} color="secondary" label={label} variant={variant || "outlined"} fullWidth />
      )}
    />
  );
};

export default CaseIntakesAutocomplete;

interface CaseIntakesMultipleAutocompleteProps {
  caseId: number;
  label: string;
  variant?: TextFieldProps["variant"];
  selected?: DocumentIntake[];
  onChange: (intakes: DocumentIntake[]) => void;
  className?: string;
}

export const CaseIntakesMultipleAutocomplete = (props: CaseIntakesMultipleAutocompleteProps) => {
  const { onChange, label, variant, selected, className } = props;
  const [textQuery, setTextQuery] = useState<string>("");
  const options = useAppSelector(selectIntakesSortedByStatus);

  return (
    <StyledAutocomplete
      className={className}
      freeSolo={false}
      multiple
      autoHighlight
      autoComplete
      size="small"
      fullWidth
      options={options}
      value={selected}
      onChange={(_evt, item) => {
        onChange(item || []);
      }}
      inputValue={textQuery}
      groupBy={(i) => documentStatusText(i.status)}
      onInputChange={(_evt, newInputValue) => setTextQuery(newInputValue)}
      renderOption={renderIntake}
      getOptionLabel={(d: DocumentIntake) => truncateString(d.name, 25)}
      filterOptions={createFilterOptions({
        ignoreCase: true,
        matchFrom: "any",
        stringify: (i) => i.name,
      })}
      renderInput={(params) => (
        <TextField {...params} label={`${label} (${options.length})`} variant={variant || "outlined"} fullWidth />
      )}
    />
  );
};
